/**
 * Centralized UI state persistence using localStorage
 * 
 * This module provides a consistent API for persisting various UI state
 * across page refreshes and browser sessions.
 */

// Storage keys
const STORAGE_KEYS = {
  SIDEBAR_OPEN: 'sidebar-open',
  SIDEBAR_WIDTH: 'sidebar-width',
  EXPANDED_PROJECTS: 'sidebar-expanded-projects',
  EXPANDED_FAVORITES: 'sidebar-expanded-favorites',
} as const;

// Default values
const DEFAULTS = {
  SIDEBAR_OPEN: false,
  SIDEBAR_WIDTH: 256, // 16rem in pixels
  EXPANDED_PROJECTS: [] as string[],
  EXPANDED_FAVORITES: true,
} as const;

// Bounds for numeric values
const BOUNDS = {
  SIDEBAR_WIDTH: {
    MIN: 200,
    MAX: 480,
  },
} as const;

/**
 * Generic storage functions
 */
function getItem<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') {
    return defaultValue;
  }

  try {
    const stored = localStorage.getItem(key);
    if (stored === null) {
      return defaultValue;
    }
    return JSON.parse(stored) as T;
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return defaultValue;
  }
}

function setItem<T>(key: string, value: T): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
}

/**
 * Sidebar open/collapsed state
 */
export function getSidebarOpen(): boolean {
  return getItem(STORAGE_KEYS.SIDEBAR_OPEN, DEFAULTS.SIDEBAR_OPEN);
}

export function setSidebarOpen(open: boolean): void {
  setItem(STORAGE_KEYS.SIDEBAR_OPEN, open);
}

/**
 * Sidebar width
 */
export function getSidebarWidth(): number {
  const width = getItem(STORAGE_KEYS.SIDEBAR_WIDTH, DEFAULTS.SIDEBAR_WIDTH);
  // Validate bounds
  if (width < BOUNDS.SIDEBAR_WIDTH.MIN || width > BOUNDS.SIDEBAR_WIDTH.MAX) {
    return DEFAULTS.SIDEBAR_WIDTH;
  }
  return width;
}

export function setSidebarWidth(width: number): void {
  // Clamp to bounds
  const clampedWidth = Math.max(
    BOUNDS.SIDEBAR_WIDTH.MIN,
    Math.min(BOUNDS.SIDEBAR_WIDTH.MAX, width)
  );
  setItem(STORAGE_KEYS.SIDEBAR_WIDTH, clampedWidth);
}

/**
 * Expanded projects in sidebar
 */
export function getExpandedProjects(): Set<string> {
  const array = getItem(STORAGE_KEYS.EXPANDED_PROJECTS, DEFAULTS.EXPANDED_PROJECTS);
  return new Set(array);
}

export function setExpandedProjects(projectIds: Set<string>): void {
  setItem(STORAGE_KEYS.EXPANDED_PROJECTS, Array.from(projectIds));
}

export function toggleExpandedProject(projectId: string): Set<string> {
  const expanded = getExpandedProjects();
  if (expanded.has(projectId)) {
    expanded.delete(projectId);
  } else {
    expanded.add(projectId);
  }
  setExpandedProjects(expanded);
  return expanded;
}

/**
 * Expanded favorites section in sidebar
 */
export function getExpandedFavorites(): boolean {
  return getItem(STORAGE_KEYS.EXPANDED_FAVORITES, DEFAULTS.EXPANDED_FAVORITES);
}

export function setExpandedFavorites(expanded: boolean): void {
  setItem(STORAGE_KEYS.EXPANDED_FAVORITES, expanded);
}

/**
 * Get sidebar width bounds
 */
export function getSidebarWidthBounds() {
  return BOUNDS.SIDEBAR_WIDTH;
}